import { useCallback, useEffect, useState } from 'react'
import { getOrgSettings, saveFromAddress } from '../api/orgSettings'

/**
 * The address replies are sent from when the ticket carries none.
 *
 * A ticket that arrived by email already says which of your addresses the
 * customer wrote to, and one raised in Desk borrows its department's. This
 * is the last resort, and on its own screen because it is the only setting
 * here that can stop a reply leaving at all.
 */
export function SendFromSection() {
  const [from, setFrom] = useState('')
  const [saved, setSaved] = useState('')
  const [canSend, setCanSend] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getOrgSettings()
      setFrom(res.automation.fromEmailAddress)
      setSaved(res.automation.fromEmailAddress)
      setCanSend(res.canSendEmail ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save() {
    setBusy(true)
    setError('')
    try {
      const res = await saveFromAddress(from.trim())
      setSaved(res.fromEmailAddress)
      setFrom(res.fromEmailAddress)
      setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Sending address</h2>
      <p className="hint">Where emailed replies come from. Usually nothing to set.</p>

      {error && <div className="error">{error}</div>}

      {canSend === false && (
        <p className="note warn">
          Zoho Desk has no support address yet, so replies cannot be emailed, only added as
          notes. Add one in Desk under Setup → Channels → Email; it takes a click and gives you
          an address ending in zohodesk.com.
        </p>
      )}

      <label className="field">
        <span>Fallback address</span>
        <input
          type="email"
          placeholder="The address the customer wrote to"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value)
            setDone(false)
          }}
          autoComplete="off"
          disabled={loading || busy}
        />
      </label>

      <p className="note">
        Used only when a ticket has no incoming email to answer and its department has no
        address of its own. Must be one Zoho Desk has set up.
      </p>

      <button onClick={() => void save()} disabled={loading || busy || from.trim() === saved}>
        {busy ? 'Saving…' : done && from.trim() === saved ? 'Saved' : 'Save address'}
      </button>
    </div>
  )
}
